import type { UiSlice } from '@/shared/lib/zustand-types';

export type AudioSlice = {
  /** Master playback volume in [0, 1]. */
  volume: number;
  /** Mute toggle. When true, `play()` is a no-op regardless of volume. */
  muted: boolean;
  setVolume: (v: number) => void;
  toggleMute: () => void;
};

const STORAGE_KEY = 'infiltre:audio:v1';
// TODO(audio): restore DEFAULT_VOLUME to 0.6 once background music ships.
// Temp-forced to 0 because there is no BGM yet and bare SFX feel naked.
const DEFAULT_VOLUME = 0;
const DEFAULT_MUTED = false;

type PersistedShape = { volume: number; muted: boolean };

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

const loadInitial = (): PersistedShape => {
  // TODO(audio): drop this override + restore persisted volume once BGM ships.
  // Forced to 0 so previously-persisted volumes don't leak SFX into a soundtrack-less build.
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw) return { volume: DEFAULT_VOLUME, muted: DEFAULT_MUTED };
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    return {
      volume: DEFAULT_VOLUME,
      muted: typeof parsed.muted === 'boolean' ? parsed.muted : DEFAULT_MUTED,
    };
  } catch {
    return { volume: DEFAULT_VOLUME, muted: DEFAULT_MUTED };
  }
};

const persist = (shape: PersistedShape): void => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(shape));
  } catch {
    /* quota / private mode — drop silently */
  }
};

export const createAudioSlice: UiSlice<AudioSlice> = (set) => {
  const initial = loadInitial();
  return {
    volume: initial.volume,
    muted: initial.muted,
    setVolume: (v) =>
      set((s) => {
        s.volume = clamp01(v);
        persist({ volume: s.volume, muted: s.muted });
      }),
    toggleMute: () =>
      set((s) => {
        s.muted = !s.muted;
        persist({ volume: s.volume, muted: s.muted });
      }),
  };
};

/** Exposed for tests that bypass the Zustand store. */
export const audioSliceInternal = {
  STORAGE_KEY,
  DEFAULT_VOLUME,
  DEFAULT_MUTED,
};
